/**
 * ToolsBundlePage
 * Public sales page at /tools-bundle for the $59.99 Complete RV Buyer Toolkit
 * (all 4 tools + Daily Matches). Checkout runs through Stripe.
 *
 * Flow:
 *   1. Visitor enters email + clicks "Buy Bundle"
 *   2. POST /api/tools/create-payment?email=... → returns Stripe checkout URL
 *   3. Visitor pays on Stripe Checkout
 *   4. Stripe redirects to /payment/success?session_id=... which polls
 *      /api/tools/execute-payment to grant the tools to the lead profile
 *   5. They can then visit /my-quiz with their email to see their tools
 *
 * All cards inlined as flat JSX to avoid the dev-server visual-edits
 * Babel plugin choking on dynamic Icon-as-prop patterns.
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Calculator, FileSearch, MessageSquare, Check, ArrowLeft, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ToolsBundlePage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleBuy = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tools/create-payment`, null, {
        params: { email: email.trim().toLowerCase(), name: name.trim() || null },
      });
      if (res.data?.approval_url) {
        // Cache email so /tools/success can identify the buyer if anything detaches
        localStorage.setItem('rv_tools_purchase_email', email.trim().toLowerCase());
        window.location.href = res.data.approval_url;
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const bg = isDark ? 'bg-gray-900' : 'bg-white';
  const cardBg = isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm';
  const textHeading = isDark ? 'text-white' : 'text-gray-900';
  const textBody = isDark ? 'text-gray-300' : 'text-gray-700';
  const textSubtle = isDark ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className={`min-h-screen ${bg}`} data-testid="tools-bundle-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Back to home */}
        <button
          onClick={() => navigate('/')}
          className={`inline-flex items-center gap-1.5 mb-6 text-sm font-medium ${textSubtle} hover:opacity-80`}
          data-testid="back-home-btn"
        >
          <ArrowLeft size={14} />
          Back to home
        </button>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left: Pitch */}
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-4"
              style={{ background: '#F2A90020', color: '#F2A900' }}
            >
              RV MATCH AI PRO TOOLS — LIFETIME ACCESS
            </div>
            <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight ${textHeading}`} data-testid="page-title">
              All 4 RV Buying Tools + Daily Matches <br />
              <span className="text-[#F2A900]">$59.99</span> <span className={`text-base sm:text-lg font-normal ${textSubtle}`}>(one-time)</span>
            </h1>
            <p className={`text-lg mb-6 ${textBody}`}>
              Stop overpaying. Stop guessing. The same AI-powered tools the pros use to lock in fair deals — for one flat price, lifetime access.
            </p>

            {/* Tool list */}
            <div className="space-y-3 mb-6">
              {/* Cost Calculator */}
              <div className={`flex items-start gap-3 rounded-xl p-3 ${cardBg}`} data-testid="bundle-tool-cost-calculator">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-green-600 ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                  <Calculator size={18} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className={`font-bold ${textHeading}`}>RV Cost Calculator</h3>
                    <span className="text-xs line-through opacity-60">$199</span>
                  </div>
                  <p className={`text-sm ${textBody}`}>See the true 5-year cost — financing, depreciation, fuel, insurance, storage, maintenance.</p>
                </div>
              </div>

              {/* Deal Analyzer */}
              <div className={`flex items-start gap-3 rounded-xl p-3 ${cardBg}`} data-testid="bundle-tool-deal-analyzer">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-blue-600 ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <FileSearch size={18} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className={`font-bold ${textHeading}`}>RV Deal Analyzer</h3>
                    <span className="text-xs line-through opacity-60">$199</span>
                  </div>
                  <p className={`text-sm ${textBody}`}>AI compares the listing against market data + similar units. Tells you steal, fair, or overpriced — with a counter-offer suggestion.</p>
                </div>
              </div>

              {/* Negotiation Coach */}
              <div className={`flex items-start gap-3 rounded-xl p-3 ${cardBg}`} data-testid="bundle-tool-negotiation-coach">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-purple-600 ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                  <MessageSquare size={18} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className={`font-bold ${textHeading}`}>RV Negotiation Coach</h3>
                    <span className="text-xs line-through opacity-60">$199</span>
                  </div>
                  <p className={`text-sm ${textBody}`}>Word-for-word dealership script. Opening line, counter-offer math, comebacks for pushback, walk-away threshold.</p>
                </div>
              </div>
            </div>

            {/* Value math */}
            <div className={`rounded-xl p-3 ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-300'}`}>
              <p className={`text-sm ${textBody}`}>
                <span className="font-bold">Total individual value: $104.95</span> ·{' '}
                <span className="text-[#F2A900] font-bold">Bundle: $59.99</span> · You save{' '}
                <span className="font-bold">$45</span>
              </p>
            </div>
          </div>

          {/* Right: Checkout */}
          <div>
            <div className={`rounded-2xl p-6 sm:p-8 shadow-2xl ${cardBg}`}>
              <h2 className={`text-2xl font-bold mb-1 ${textHeading}`}>Get instant access</h2>
              <p className={`text-sm mb-6 ${textSubtle}`}>One-time payment · Lifetime access · No subscription</p>

              <form onSubmit={handleBuy} className="space-y-3">
                <div>
                  <label className={`text-xs font-semibold uppercase tracking-wide ${textSubtle}`}>
                    First Name <span className="opacity-60 font-normal normal-case">(optional)</span>
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Sara"
                    className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                    data-testid="bundle-name-input"
                  />
                </div>
                <div>
                  <label className={`text-xs font-semibold uppercase tracking-wide ${textSubtle}`}>
                    Email <span className="text-[#F2A900]">*</span>
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                    data-testid="bundle-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full font-bold py-4 text-base bg-[#F2A900] hover:bg-[#D99700] text-black"
                  data-testid="bundle-buy-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Starting checkout…
                    </>
                  ) : (
                    <>Get Lifetime Access — $59.99</>
                  )}
                </Button>
              </form>

              {/* Trust signals */}
              <div className={`mt-6 pt-6 border-t space-y-2 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className={`flex items-center gap-2 text-xs ${textBody}`}>
                  <ShieldCheck size={14} className="text-green-500 shrink-0" />
                  Secure payment via Stripe
                </div>
                <div className={`flex items-center gap-2 text-xs ${textBody}`}>
                  <Check size={14} className="text-green-500 shrink-0" />
                  Instant access after payment
                </div>
                <div className={`flex items-center gap-2 text-xs ${textBody}`}>
                  <Check size={14} className="text-green-500 shrink-0" />
                  Tools save in your profile forever
                </div>
              </div>
            </div>

            <p className={`mt-4 text-xs text-center ${textSubtle}`}>
              Already a buyer? <Link to="/my-quiz" className="font-semibold underline">Open My Profile</Link> with your email.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolsBundlePage;
